/**
 * The access token, held in memory for the life of the tab (spec 067 FR-010).
 *
 * Deliberately not `localStorage` or `sessionStorage`: anything script-readable is
 * readable by injected script, and a token there survives the tab that earned it.
 * `oidc-client-ts` defaults to `sessionStorage`, so this is an override, not the
 * library's behaviour.
 *
 * A module-level holder rather than React state because the readers are not
 * components: `customFetch` is a plain function, and the file-preview hook issues a
 * raw `fetch`. Both need the current token at call time, outside any render.
 */
let accessToken: string | null = null;

/** Registered by the session provider so the request seam can renew on a 401. */
let renew: (() => Promise<string | null>) | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function setTokenRenewer(
  fn: (() => Promise<string | null>) | null,
): void {
  renew = fn;
}

/**
 * One renewal at a time, shared by every caller that asks while it is running.
 *
 * Without this, a board that fires ten requests gets ten 401s and starts ten
 * renewals; the first consumes the single-use refresh token and the other nine
 * fail, signing the user out in the middle of a working session. The failure
 * needs concurrency to appear, so it survives casual testing.
 */
let inFlight: Promise<string | null> | null = null;

/**
 * How long a failed renewal is remembered.
 *
 * Short on purpose: this exists to collapse one burst of 401s, not to keep a
 * session dead. A board issues several requests at once, so their 401s arrive
 * staggered rather than simultaneously — and clearing `inFlight` unconditionally
 * meant each one started its own renewal after the previous settled, so an expired
 * refresh token produced a run of duplicate "session ended" handling and redirect
 * attempts instead of a single one.
 */
const FAILED_RENEWAL_TTL_MS = 3_000;

let failedAt: number | null = null;

export function renewAccessToken(): Promise<string | null> {
  if (!renew) return Promise.resolve(null);
  if (failedAt !== null && Date.now() - failedAt < FAILED_RENEWAL_TTL_MS) {
    return Promise.resolve(null);
  }
  inFlight ??= renew()
    .then((token) => {
      failedAt = token === null ? Date.now() : null;
      return token;
    })
    .catch((error: unknown) => {
      failedAt = Date.now();
      throw error;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/** Test seam: forget any in-flight renewal between cases. */
export function resetTokenStore(): void {
  accessToken = null;
  renew = null;
  inFlight = null;
  failedAt = null;
}
